import * as React from "react"
import { Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

const MIN_REASON_LENGTH = 10

export type FieldValue = string | number | boolean | undefined

export type FieldSpec =
  | {
      type: "text" | "number" | "date"
      name: string
      label: string
      placeholder?: string
      help?: string
      required?: boolean
      wide?: boolean
    }
  | {
      type: "select"
      name: string
      label: string
      options: { value: string; label: string }[]
      help?: string
      required?: boolean
      wide?: boolean
    }
  | { type: "checkbox"; name: string; label: string; help?: string; wide?: boolean }

interface FieldsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  fields: FieldSpec[]
  initial: Record<string, FieldValue>
  pending?: boolean
  reasonSuggestions?: string[]
  /**
   * Bloque propio del formulario, bajo los campos y sobre el motivo.
   *
   * Existe para lo que este dialogo NO sabe modelar: una lista REPETIBLE. Los
   * campos de aca son escalares —texto, numero, fecha, seleccion— y las lineas
   * de un contrato son N filas con cinco columnas cada una. Meterlas en
   * `FieldSpec` habria vuelto generico algo que se usa en una sola pantalla, y
   * el resto de los maestros habria cargado con esa complejidad.
   */
  extra?: React.ReactNode
  /**
   * Motivo por el que no se puede guardar, si lo hay. La regla vive donde estan
   * los datos —quien renderiza `extra`— y esto solo la muestra: el boton dice
   * QUE falta en vez de estar apagado sin explicacion.
   */
  blockedReason?: string
  onSubmit: (values: Record<string, FieldValue>, reason: string) => void | Promise<void>
}

/**
 * Formulario generico de maestros. Siempre con motivo obligatorio:
 * no hay guardado silencioso en ninguna pantalla.
 */
export function FieldsDialog({
  open,
  onOpenChange,
  title,
  description,
  fields,
  initial,
  pending,
  reasonSuggestions,
  extra,
  blockedReason,
  onSubmit,
}: FieldsDialogProps) {
  const [values, setValues] = React.useState<Record<string, FieldValue>>(initial)
  const [reason, setReason] = React.useState("")
  const [touched, setTouched] = React.useState(false)

  React.useEffect(() => {
    if (open) {
      setValues(initial)
      setReason("")
      setTouched(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const reasonInvalid = reason.trim().length < MIN_REASON_LENGTH
  const missingRequired = fields.some(
    (field) =>
      "required" in field &&
      field.required &&
      (values[field.name] === undefined || values[field.name] === ""),
  )

  function setValue(name: string, value: FieldValue) {
    setValues((prev) => ({ ...prev, [name]: value }))
  }

  async function handleSubmit() {
    setTouched(true)
    if (reasonInvalid || missingRequired || blockedReason) return
    await onSubmit(values, reason.trim())
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          {fields.map((field) => (
            <div
              key={field.name}
              className={cn("space-y-1", field.wide ? "sm:col-span-2" : undefined)}
            >
              {field.type === "checkbox" ? (
                <label className="flex items-center gap-2 pt-4 text-xs">
                  <Checkbox
                    checked={Boolean(values[field.name])}
                    onCheckedChange={(checked) => setValue(field.name, Boolean(checked))}
                  />
                  {field.label}
                </label>
              ) : (
                <>
                  <Label htmlFor={`field-${field.name}`}>
                    {field.label}
                    {"required" in field && field.required ? (
                      <span className="text-destructive"> *</span>
                    ) : null}
                  </Label>
                  {field.type === "select" ? (
                    <Select
                      value={values[field.name] === undefined ? "" : String(values[field.name])}
                      onValueChange={(value) => setValue(field.name, value)}
                    >
                      <SelectTrigger id={`field-${field.name}`}>
                        <SelectValue placeholder={t("Selecciona")} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={`field-${field.name}`}
                      type={field.type === "number" ? "number" : field.type}
                      value={values[field.name] === undefined ? "" : String(values[field.name])}
                      placeholder={field.placeholder}
                      onChange={(event) =>
                        setValue(
                          field.name,
                          field.type === "number"
                            ? event.target.value === ""
                              ? undefined
                              : Number(event.target.value)
                            : event.target.value,
                        )
                      }
                    />
                  )}
                </>
              )}
              {field.help ? (
                <p className="text-2xs text-muted-foreground">{field.help}</p>
              ) : null}
            </div>
          ))}
        </div>

        {extra}

        <div className="space-y-1">
          <Label htmlFor="fields-motivo">
            {t("Motivo del cambio")}{" "}<span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="fields-motivo"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={t("Que cambia y por que. Se guarda con tu nombre y la fecha.")}
            className="min-h-[4rem]"
            aria-invalid={touched && reasonInvalid}
          />
          {reasonSuggestions?.length ? (
            <div className="flex flex-wrap gap-1">
              {reasonSuggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setReason(suggestion)}
                  className="rounded border px-1.5 py-0.5 text-2xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}
          <p className="text-2xs text-muted-foreground">
            {t("Obligatorio. La proyeccion se recalcula; los periodos cerrados no se tocan.")}</p>
        </div>

        <DialogFooter className="items-center">
          {blockedReason ? (
            <p className="text-2xs text-destructive sm:mr-auto">{blockedReason}</p>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={pending}>
            {t("Cancelar")}</Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={pending || reasonInvalid || missingRequired || Boolean(blockedReason)}
          >
            {pending ? <Loader2 className="animate-spin" /> : null}
            {t("Guardar con motivo")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
